import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../shared/Header/Header';
import BackButton from '../shared/backButton/BackButton';
import Footer from '../shared/Footer/Footer';
import DocxView from './DocxView';
import useIsLTE650 from '../../hooks/useIsLTE650';
import './customersPage.scss';

type SectionKey = 'policy' | 'offer' | 'return';
const SECTION_PARAM = 'section';

// Пути к файлам в /public/doc
const POLICY_DOC = '/doc/Политика о персональных данных ООО ИНТРЕЙД.docx';
const RETURN_RULES_DOC = '/doc/Возврат товара надлежащего качества.docx';
const RETURN_FORM_DOC = '/doc/Заявление для возврата товара надлежащего качества.docx';
const OFFER_DOC = '/doc/Договор дистанционной розничной купли-продажи.docx';

const SECTIONS: Array<{ id: SectionKey; label: string }> = [
    { id: 'policy', label: 'Политика' },
    { id: 'offer', label: 'Оферта' },
    { id: 'return', label: 'Возврат' },
];

const CustomersPage: React.FC = () => {
    const initial = useMemo<SectionKey>(() => {
        const url = new URL(window.location.href);
        const fromUrl = (url.searchParams.get(SECTION_PARAM) || '').toLowerCase();
        if (fromUrl === 'offer') return 'offer';
        if (fromUrl === 'return') return 'return';
        return 'policy';
    }, []);

    const isMobile = useIsLTE650(650);
    const [active, setActive] = useState<SectionKey>(initial);

    // Синхронизация параметра при первом рендере
    useEffect(() => {
        const url = new URL(window.location.href);
        if (url.searchParams.get(SECTION_PARAM) !== active) {
            url.searchParams.set(SECTION_PARAM, active);
            window.history.replaceState({ section: active }, '', url.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Навигация назад/вперёд
    useEffect(() => {
        const onPopState = () => {
            const url = new URL(window.location.href);
            const s = (url.searchParams.get(SECTION_PARAM) || '').toLowerCase();
            setActive(s === 'offer' ? 'offer' : s === 'return' ? 'return' : 'policy');
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const switchTo = (id: SectionKey) => {
        if (id === active) return;
        setActive(id);
        const url = new URL(window.location.href);
        url.searchParams.set(SECTION_PARAM, id);
        window.history.pushState({ section: id }, '', url.toString());
        // мягко прокрутим к началу панели
        requestAnimationFrame(() => {
            document.querySelector('.customersPage__panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    const pageTitle = useMemo(() => {
        switch (active) {
            case 'policy':
                return 'Покупателям — Политика персональных данных | S-Gabai';
            case 'offer':
                return 'Покупателям — Публичная оферта | S-Gabai';
            case 'return':
                return 'Покупателям — Возврат товара | S-Gabai';
            default:
                return 'Покупателям | S-Gabai';
        }
    }, [active]);

    const pageDescription = 'Политика персональных данных, публичная оферта, условия возврата и форма заявления — всё для покупателей S-Gabai на одной странице.';

    const renderSection = () => {
        switch (active) {
            case 'policy':
                return (
                    <section key="policy" className="customersPage__panel" aria-labelledby="section-policy">
                        {/* <h2 id="section-policy" className="customersPage__panel_title">
                            Политика персональных данных
                        </h2> */}
                        <DocxView src={POLICY_DOC} title="Политика о персональных данных (docx)" />
                    </section>
                );
            case 'offer':
                return (
                    <section key="offer" className="customersPage__panel" aria-labelledby="section-offer">
                        {/* <h2 id="section-offer" className="customersPage__panel_title">
                            Публичная оферта
                        </h2> */}
                        <DocxView src={OFFER_DOC} title="Публичная оферта (docx)" />
                    </section>
                );
            case 'return':
                return (
                    <section key="return" className="customersPage__panel" aria-labelledby="section-return">
                        {/* <h2 id="section-return" className="customersPage__panel_title">
                            Возврат товара
                        </h2> */}

                        <DocxView src={RETURN_RULES_DOC} title="Возврат товара (docx)" />

                        <div id="doc-return-form" className="customersPage__panel_actions customersPage__panel_actions--gapTop">
                            {/* <a className="customersPage__btn customersPage__btn--accent" href={encodeURI(RETURN_FORM_DOC)} target="_blank" rel="noopener noreferrer">
                                Открыть бланк заявления (.docx)
                            </a> */}
                            <a className="customersPage__btn customersPage__btn--primary" href={encodeURI(RETURN_FORM_DOC)} download>
                                Скачать бланк возврата .docx
                            </a>
                        </div>
                    </section>
                );
            default:
                return null;
        }
    };

    return (
        <div className="customersPage">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
            </Helmet>

            <Header isMobile={isMobile} />
            <main className="customersPage__main">
                <div className="customersPage__main_titleBox">
                    <BackButton />
                    <h1 className="customersPage__main_title">Покупателям</h1>
                </div>
                <div className="customersPage__main_control" role="tablist" aria-label="Разделы для покупателей">
                    {SECTIONS.map(({ id, label }) => {
                        const isActive = id === active;
                        return (
                            <button
                                key={id}
                                role="tab"
                                aria-selected={isActive}
                                aria-controls={`section-${id}`}
                                id={`tab-${id}`}
                                className={`customersPage__main_control_elem${isActive ? ' customersPage__main_control_elem--active' : ''}`}
                                type="button"
                                onClick={() => switchTo(id)}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>

                {renderSection()}
            </main>
            <Footer isMobile={isMobile} />
        </div>
    );
};

export default CustomersPage;
